/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Row } from "react-bootstrap";
import { Button, Table, Alert, Input, FieldGroup } from "core/components";
import { ProjectFormData, ProjectRequisitionsSchema } from "core/schema";
import { api, ApiError } from "core/api";
import { change_order_requisitions_admin_project_path, requisitions_admin_project_path } from "routes";
import { Controller, useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import { yupResolver } from "@hookform/resolvers/yup";
import { IMaskInput } from "react-imask";
import moment from "moment";

type ProjectChangeOrderRequisitionFormProps = {
  project: ProjectFormData;
};

const ProjectChangeOrderRequisitionForm = ({
  project,
  onSubmit,
  is_admin_or_architect,
  is_engineer
}: ProjectChangeOrderRequisitionFormProps): JSX.Element => {
  const [requisitions, setRequisitions] = React.useState([]);
  const [skipPageReset, setSkipPageReset] = React.useState(false);
  const table = React.useRef(null);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { isSubmitting, isValidating, errors }
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(ProjectRequisitionsSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const getRequisitions = async () => {
    try {
      const response = await api.get(change_order_requisitions_admin_project_path({ id: project.id }));
      setRequisitions(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    getRequisitions();
  }, []);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Task ID",
        accessor: "task.code"
      },
      {
        id: 2,
        Header: "Task Name",
        accessor: "task.name"
      },
      {
        id: 3,
        Header: "Item ID",
        accessor: "item.code"
      },
      {
        id: 4,
        Header: "Item",
        accessor: "item.name"
      },
      {
        Header: "ID",
        accessor: "change_order.code",
        Cell: (props) => {
          return (
            <p className="text-center text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      },
      {
        Header: "Task",
        accessor: "task",
        Cell: (props) => {
          const task = props.value;

          return (
            <>
              <div className="text-bold text-truncate" title={task.code} data-bs-toggle="tooltip">
                {task.code}
              </div>
              <p className="text-truncate" title={task.name} data-bs-toggle="tooltip">
                {task.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Inventory ID",
        accessor: "item",
        style: {
          width: "20%"
        },
        Cell: (props) => {
          const item = props.value;
          const stockable = item.stockable;
          const label = stockable ? "STOCKABLE" : "NOT STOCKABLE";
          return (
            <>
              <span className="text-bold" title={item.code} data-bs-toggle="tooltip">
                {item.code}{" "}
                <span
                  className={`badge badge--pill badge--${stockable ? "success" : "draft"}`}
                  style={{ flex: 1, justifyContent: "flex-start", fontSize: "9px" }}
                  title={label}
                  data-bs-toggle="tooltip"
                >
                  {label}
                </span>
              </span>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={item.name} data-bs-toggle="tooltip">
                {item.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Budgeted Quantity",
        accessor: "budgeted_quantity",
        Cell: (props) => {
          setValue(`data.${props.row.index}.project_id`, project.id);
          setValue(`data.${props.row.index}.source`, "change_order");
          setValue(`data.${props.row.index}.project_task_item_id`, props.row.original.id);
          setValue(`data.${props.row.index}.inventory_quantity`, props.row.original.inventory_quantity);
          setValue(`data.${props.row.index}.budgeted_quantity`, props.value);
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Actual Quantity",
        accessor: "actual_quantity",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Remaining Quantity",
        accessor: "remaining_quantity",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Current Inventory",
        accessor: "inventory_quantity",
        Cell: (props) => {
          return (
            <div className="text-center text-truncate" title={props.value} data-bs-toggle="tooltip">
              <p className="text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Request Quantity",
        accessor: "requested_quantity",
        Cell: (props) => {
          const canRequest = props.row.original.can_request;
          const inventoryQuantity = props.row.original.inventory_quantity || getValues(`data.${props.row.index}.inventory_quantity`);

          return (
            <Controller
              name={`data.${props.row.index}.requested_quantity`}
              control={control}
              render={({ field, fieldState }) => (
                <FieldGroup errorMessage={props.errors.data?.[props.row.index]?.requested_quantity?.message}>
                  <IMaskInput
                    mask={Number}
                    padFractionalZeros={true}
                    thousandsSeparator=","
                    radix="."
                    value={field.value?.toString()}
                    unmask={true}
                    inputRef={field.ref}
                    onAccept={(value) => {
                      if (value && inventoryQuantity > 0) {
                        setValue(`data.${props.row.index}.date_needed`, moment().toDate());
                      } else if (value && inventoryQuantity < 1) {
                        setValue(`data.${props.row.index}.date_needed`, moment().add(7, "days").toDate());
                      } else {
                        setValue(`data.${props.row.index}.date_needed`, null);
                      }
                      field.onChange(value);
                    }}
                    className="field"
                    placeholder={canRequest ? "0.0" : "No stock"}
                    min={0}
                    max={9999999}
                    readOnly={!canRequest}
                    style={fieldState.error ? { borderColor: "red" } : {}}
                  />
                </FieldGroup>
              )}
            />
          );
        }
      },
      {
        Header: "Date Needed",
        accessor: "date_needed",
        Cell: (props) => {
          const canRequest = props.row.original.can_request;

          return (
            <Controller
              name={`data.${props.row.index}.date_needed`}
              control={control}
              render={({ field, fieldState }) => (
                <div className={`${fieldState.error ? "field-group--invalid" : ""}`}>
                  <DatePicker
                    selected={field.value}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    onBlur={(e) => {
                      field.onBlur(e);
                    }}
                    ref={field.ref}
                    className="field"
                    minDate={moment().toDate()}
                    placeholder={canRequest ? "Select Date" : "No stock"}
                    readOnly={!canRequest}
                  />
                </div>
              )}
            />
          );
        }
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: (props) => {
          const canRequest = props.row.original.can_request;

          return <Input {...register(`data.${props.row.index}.remarks`)} readOnly={!canRequest} />;
        }
      }
    ],
    []
  );

  const updateData = (rowIndex, columnId, value) => {
    setSkipPageReset(true);
    setRequisitions((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value
          };
        }
        return row;
      })
    );
  };

  const save = async (data) => {
    const requisitions = data.data.filter((datum) => datum.requested_quantity);

    try {
      if (requisitions.length > 0) {
        await api.post(requisitions_admin_project_path({ id: project.id }), {
          requisitions
        });
      }
      onSubmit();
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    console.log(data);
    if (data?.data) {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const dataFilter = () => {
    return (
      <>
        <Col>
          <Input placeholder="Search for Task ID" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Task Name" onChange={(e) => table.current.setFilter(2, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Item ID" onChange={(e) => table.current.setFilter(3, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Item Name" onChange={(e) => table.current.setFilter(4, e.target.value)} />
        </Col>
      </>
    );
  };

  return (
    <React.StrictMode>
      {requisitions.length > 0 ? (
        <>
          <Col>
            {formErrors && formErrors.length != 0 && (
              <Alert>
                {formErrors.map((error, index) => {
                  return <div key={index}>{error}</div>;
                })}
              </Alert>
            )}
          </Col>
          <Row className="mb-3" style={{ paddingLeft: 0, paddingRight: 0 }}>
            {dataFilter()}
            <Col className="d-flex justify-content-end align-items-baseline">
              <Button
                className="button--default mr-2"
                icon="times"
                title="Cancel"
                type="button"
                onClick={() => {
                  onSubmit();
                }}
              />
              {(is_admin_or_architect || is_engineer) && (
                <Button
                  className="button--brand"
                  icon="save"
                  title="Submit Change Request Requisition"
                  type="button"
                  loading={isSubmitting || isValidating}
                  onClick={() => {
                    handleSubmit(async (data) => await save(data), onError)();
                  }}
                />
              )}
            </Col>
          </Row>

          <Table
            columns={columns}
            data={requisitions}
            updateData={updateData}
            control={control}
            register={register}
            skipPageReset={skipPageReset}
            errors={errors}
            hiddenColumns={[1, 2, 3, 4]}
            ref={table}
          />
        </>
      ) : (
        <>
          <div className="d-flex justify-content-end align-items-baseline">
            <Button
              className="button--default"
              icon="times"
              title="Cancel"
              type="button"
              onClick={() => {
                onSubmit();
              }}
            />
          </div>
          <div className="text-center mt-4 mb-4">No available items for requisition.</div>
        </>
      )}
    </React.StrictMode>
  );
};

export default ProjectChangeOrderRequisitionForm;
